import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback, useRef } from 'react';
import { matchPath, useNavigate, useLocation } from 'react-router-dom';
import { Shop } from '../types';
import { shopsService } from '../services/api';
import safeStorage from '../utils/safeStorage';
import { primeShopTimezone } from '../hooks/useShopTimezones';

const SHOP_STORAGE_KEY = 'selectedShopSlug';
const SHOP_FREE_PATHS = ['/shops', '/mechanix-hub', '/forgot-password', '/reset-password'];
const SHOP_FREE_PREFIXES = ['/track/', '/pay/'];

interface ShopContextProps {
  selectedShop: Shop | null;
  isLoadingShop: boolean;
  shopError: string | null;
  setSelectedShopBySlug: (slug: string) => Promise<void>;
  clearSelectedShop: () => void;
}

interface ShopProviderProps {
  children: ReactNode;
}

const ShopContext = createContext<ShopContextProps | undefined>(undefined);

export const useShop = (): ShopContextProps => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};

export const ShopProvider: React.FC<ShopProviderProps> = ({ children }) => {
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [isLoadingShop, setIsLoadingShop] = useState(true); // Start loading initially
  const [shopError, setShopError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation(); // Add location to check current path
  // The provider wraps <Routes> rather than living inside a matched <Route>,
  // so useParams() here is always empty — which silently ignored every
  // /shop/:slug deep link (the shop's public booking link included) in a
  // fresh browser. Match the path directly instead.
  const urlShopSlug = matchPath({ path: '/shop/:shopSlug', end: false }, location.pathname)
    ?.params.shopSlug;
        
  // Function to fetch shop details from a slug
  const fetchShopBySlug = useCallback(async (slug: string): Promise<Shop | null> => {
    try {
      setIsLoadingShop(true);
      setShopError(null);
      // Resolved server-side. This used to download every shop and match the
      // slug in the browser, on every navigation that changed the slug or path.
      const shop = await shopsService.getShopBySlug(slug);
      primeShopTimezone(shop.id, shop.timezone);
      return shop;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setShopError(`Shop "${slug}" not found`);
      } else {
        console.error('Failed to fetch shop by slug', error);
        setShopError('Failed to load shop information');
      }
      return null;
    } finally {
      setIsLoadingShop(false);
    }
  }, []);

  // Routes that work without a shop: the picker, the hub, and the links
  // customers open from email — a fresh browser has no stored shop, and these
  // used to bounce it to the picker.
  const isPublicRoute =
    SHOP_FREE_PATHS.includes(location.pathname) ||
    SHOP_FREE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  const slugToLoad: string | null = urlShopSlug || safeStorage.getItem(SHOP_STORAGE_KEY);
  // The slug `selectedShop` was resolved from. Moving between screens of the
  // same shop must not resolve it again: that flipped `isLoadingShop`, so
  // RouteGuard unmounted the page, and the page then loaded its data twice.
  const loadedSlug = useRef<string | null>(null);

  // Load the shop from the URL or storage on mount, and when the slug changes
  useEffect(() => {
    // If we're on a public route that doesn't require shop, don't auto-load a shop
    if (isPublicRoute) {
      setIsLoadingShop(false);
      return;
    }
    if (slugToLoad && slugToLoad === loadedSlug.current) return;

    let cancelled = false;
    const initializeShop = async () => {
      if (!slugToLoad) {
        // No shop in URL or storage
        loadedSlug.current = null;
        setSelectedShop(null);
        setIsLoadingShop(false);
        navigate('/shops', { replace: true });
        return;
      }

      const shop = await fetchShopBySlug(slugToLoad);
      if (cancelled) return;
      if (shop) {
        loadedSlug.current = slugToLoad;
        setSelectedShop(shop);
        // Ensure storage is updated if loaded via URL
        if (urlShopSlug && urlShopSlug !== safeStorage.getItem(SHOP_STORAGE_KEY)) {
          safeStorage.setItem(SHOP_STORAGE_KEY, urlShopSlug);
        }
      } else {
        // Shop not found or error occurred
        loadedSlug.current = null;
        safeStorage.removeItem(SHOP_STORAGE_KEY); // Clear invalid stored slug
        setSelectedShop(null);
        navigate('/shops', { replace: true });
      }
    };

    initializeShop();
    return () => {
      cancelled = true;
    };
  }, [isPublicRoute, slugToLoad, urlShopSlug, fetchShopBySlug, navigate]);

  // Function to explicitly set the selected shop and store it
  const setSelectedShopBySlug = useCallback(async (slug: string) => {
    const shop = await fetchShopBySlug(slug);
    if (shop) {
      loadedSlug.current = slug;
      setSelectedShop(shop);
      safeStorage.setItem(SHOP_STORAGE_KEY, slug);
      setShopError(null); // Clear previous errors
    } else {
      // Handle case where the provided slug is invalid
      // Error is set within fetchShopBySlug
      loadedSlug.current = null;
      setSelectedShop(null);
      safeStorage.removeItem(SHOP_STORAGE_KEY);
    }
  }, [fetchShopBySlug]);

  // Function to clear the selected shop - only called explicitly from ShopsListPage
  const clearSelectedShop = useCallback(() => {
    loadedSlug.current = null;
    setSelectedShop(null);
    safeStorage.removeItem(SHOP_STORAGE_KEY);
    setShopError(null);
  }, []);

  const value = {
    selectedShop,
    isLoadingShop,
    shopError,
    setSelectedShopBySlug,
    clearSelectedShop,
  };

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}; 